<template>
    <InfoBaseForm
        v-if="formData"
        ref="baseFormRef"
        v-model="formData"
        :is-loading="isLoading"
        data-cy="info-meal-types-form"
        @save="save()"
        @cancel="emit('cancel')"
    >
        <template v-slot:center-block>
            <v-row dense class="mb-4">
                <v-col cols="12" sm="6" md="4">
                    <v-autocomplete
                        v-model="formData.type"
                        :items="secondaryTypes"
                        :label="`${$t('type')}*`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="type"
                        item-title="name"
                        item-value="otaCode"
                        :rules="[requiredRule('type')]"
                        :readonly="isHotelDescriptionMode"
                    />
                    <v-checkbox
                        v-model="formData.useAsDefault"
                        :label="$t('product.info.default')"
                        density="compact"
                        color="primary"
                        data-cy="is-default"
                        hide-details
                        :disabled="formData.useAsDefault && !!editSecondaryService"
                        :readonly="isHotelDescriptionMode"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                    <v-text-field
                        v-model="formData.mealCode"
                        :label="$t('meal_code')"
                        variant="outlined"
                        color="primary"
                        density="compact"
                        data-cy="meal-code"
                        :readonly="isHotelDescriptionMode"
                    />
                </v-col>
            </v-row>
            <v-row v-if="formData.hasOwnProperty('shortDescription') && baseFormRef?.isShowEditors" dense>
                <v-col cols="12" md="8">
                    <div class="text-subtitle-1">{{ $t('descriptions.short') }}:</div>
                    <NewHtmlEditor
                        v-model="formData.shortDescription[baseFormRef?.activeLangCode]"
                        :toolbar="isHotelDescriptionMode ? {items: []} : undefined"
                        class="mb-2"
                    >
                        <div
                            v-if="baseFormRef?.activeLangCode"
                            class="fi fill-height px-3"
                            :class="[`fi-${useFlagIconCode(baseFormRef?.activeLangCode)}`]"
                        />
                    </NewHtmlEditor>
                </v-col>
            </v-row>
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {initInfoMealTypes} from '~/utils/types/account/products/helpers'
    import InfoBaseForm from '~src/components/products/information/InfoBaseForm.vue'
    import {useFlagIconCode} from '~/composables/helpers'
    import {clone} from '~/utils/helpers'
    const NewHtmlEditor = defineAsyncComponent(() => import('~/components/admin/NewHtmlEditor.vue'))

    const props = defineProps<{
        editSecondaryService: any | null
        secondaryTypes: any[]
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        // eslint-disable-next-line @typescript-eslint/unified-signatures
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const formData = ref(initInfoMealTypes())
    const baseFormRef = ref(null)
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)

    onMounted(async () => {
        if (props.editSecondaryService) formData.value = clone(props.editSecondaryService)
    })

    const save = async (): Promise<void> => {
        const data = clone(formData.value)
        clean(data)
        try {
            isLoading.value = true
            if (props.editSecondaryService?.id) {
                await app.$api.hotelDescriptionMealType.put(supplierStore.hotelInfo.hotelId, data.id, {
                    ...data,
                })
            } else {
                await app.$api.hotelDescriptionMealType.post(supplierStore.hotelInfo.hotelId, {
                    ...data,
                })
            }
            if (!isHotelDescriptionMode) await supplierStore.loadHotelSecondaryServices()
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
